import React, { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { capitalizeFirstLetter } from '../utils/capitalize';
import { useTranslation } from 'react-i18next';

export interface OptionType {
    label: string;
    value: string;
    group?: string;
}

interface DropDownProps {
    handleChange: (event: React.SyntheticEvent, value: OptionType | null) => void;
    selectedItem?: OptionType | null;
    items: OptionType[];
    inputLabel: string;
    disabled?: boolean;
    selectClassName?: string;
    formClassName?: string;
    grouped?: boolean;
    loading?: boolean;
    warning?: boolean;
    warningChar?: string;
    warningColor?: string;
    infos?: boolean;
    infosColor?: string;
    sortItems?: boolean;
    sortGroups?: boolean;
    disableClearable?: boolean;
    required?: boolean;
    placeholder?: string;
    helperText?: string;
}

const DropDown: React.FC<DropDownProps> = React.memo(
    ({
        handleChange,
        selectedItem,
        items,
        inputLabel,
        disabled = false,
        selectClassName = '',
        formClassName = '',
        grouped = false,
        loading = false,
        warning = false,
        warningChar,
        warningColor,
        sortItems = true,
        sortGroups = true,
        infos = false,
        infosColor = '#8888DD',
        disableClearable = true,
        required = false,
        placeholder,
        helperText,
    }) => {
        const { t } = useTranslation();
        const sortedItems = useMemo(() => {
            return sortItems
                ? [...items].sort((a, b) => {
                      const labelA = a.label.toLowerCase();
                      const labelB = b.label.toLowerCase();
                      if (labelA === 'sltp') return -1;
                      if (labelB === 'sltp') return 1;
                      return labelA.localeCompare(labelB);
                  })
                : items;
        }, [items, sortItems]);

        const groupedItems = useMemo(() => {
            const grouped = sortedItems.reduce(
                (acc, item) => {
                    const groupName = item.group || '---';
                    if (!acc[groupName]) {
                        acc[groupName] = [];
                    }
                    acc[groupName].push(item);
                    return acc;
                },
                {} as Record<string, OptionType[]>
            );

            const sortedGroupNames = Object.keys(grouped).sort((a, b) => (sortGroups ? a.localeCompare(b) : 0));

            return sortedGroupNames.flatMap((groupName) => grouped[groupName]);
        }, [sortedItems]);

        const handleAutocompleteChange = (event: React.SyntheticEvent, value: OptionType | null) => {
            if (value && (value.value !== selectedItem?.value || value.label !== selectedItem?.label)) {
                handleChange(event, value);
            }
        };

        return (
            <div className={`pad-5px w-100 ${formClassName}`}>
                <Autocomplete
                    value={selectedItem}
                    onChange={handleAutocompleteChange}
                    options={grouped ? groupedItems : sortedItems}
                    getOptionLabel={(option) => option.label || ''}
                    getOptionKey={(option) => option.value}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={capitalizeFirstLetter(inputLabel)}
                            variant="outlined"
                            fullWidth
                            disabled={disabled}
                            className={`${selectClassName}`}
                            placeholder={placeholder}
                            helperText={helperText}
                            size="small"
                         
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: infos ? infosColor : undefined,
                                },
                            }}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: {
                                    ...params.InputProps,
                                    classes: { notchedOutline: warning ? 'autocomplete-warning' : '' },
                                    startAdornment: warning ? (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                width: 13,
                                                height: 13,
                                                top: -5,
                                                left: -5,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                borderRadius: 10,
                                                color: 'black',
                                                fontSize: 10,
                                                backgroundColor: warning ? warningColor || 'darkorange' : undefined,
                                                zIndex: 1,
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {warningChar?.charAt(0) || '!'}
                                        </div>
                                    ) : null,
                                },
                            }}
                            required={required}
                        />
                    )}
                    groupBy={grouped ? (option) => option.group || '---' : undefined}
                    disabled={disabled}
                    disableClearable={disableClearable}
                    className={selectClassName}
                    loading={loading}
                    classes={{ inputRoot: `${selectClassName}` }}
                    onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'clear') {
                            handleChange(event, null);
                            return;
                        }
                    }}
                />
            </div>
        );
    }
);

export default DropDown;
